import React from "react";
import { motion } from "framer-motion";
import { EyeOff, Flame, Code2, TrendingDown, Clock, Briefcase } from "lucide-react";

const POINTS = [
  {
    icon: EyeOff,
    title: "Can't watch screens all day",
    body: "You have a job, clients, meetings. By the time you check the chart, the move is over — or worse, it reversed on you.",
    size: "lg",
    accent: "#084392",
  },
  {
    icon: Flame,
    title: "FOMO entries",
    body: "Candle rips up. You jump in. It reverses.",
    size: "sm",
    accent: "#ef4444",
  },
  {
    icon: TrendingDown,
    title: "Panic exits",
    body: "Phone rings. You book a tiny loss. 20 minutes later it hit target.",
    size: "sm",
    accent: "#ef4444",
  },
  {
    icon: Code2,
    title: "Every other platform wants you to code.",
    body: "Python scripts. Pine Script. JSON configs. Broker APIs. You just wanted to buy a call option.",
    size: "md",
    accent: "#084392",
  },
  {
    icon: Briefcase,
    title: "Trading is a side income",
    body: "Not a full-time monitor-glued career. You need discipline on autopilot.",
    size: "md",
    accent: "#084392",
  },
  {
    icon: Clock,
    title: "Hundreds of features, zero clarity",
    body: "Greek decay. IV crush. Max pain. Half the platform is jargon you didn't sign up to learn.",
    size: "md",
    accent: "#0f172a",
  },
];

export default function PainPoints() {
  return (
    <section
      id="problem"
      data-testid="pain-points-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.2em]">
            / THE PROBLEM
          </div>
          <h2 className="font-heading mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            You&rsquo;re not a bad trader.
            <br />
            <span className="text-[#084392]">You&rsquo;re just human.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl leading-relaxed">
            90% of retail traders lose not because of bad analysis, but because
            emotions and real life get in the way of execution.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
          {POINTS.map((p, i) => {
            const Icon = p.icon;
            const colSpan =
              p.size === "lg"
                ? "md:col-span-4"
                : p.size === "md"
                ? "md:col-span-2"
                : "md:col-span-2";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`pain-card-${i}`}
                className={`${colSpan} group relative rounded-2xl border border-[#e2e8f0] bg-white p-6 md:p-8 hover:border-[#084392]/40 transition`}
              >
                <div
                  className="h-11 w-11 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: `${p.accent}15`, color: p.accent }}
                >
                  <Icon size={20} />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-[#0f172a]">
                  {p.title}
                </h3>
                <p className="mt-2 text-[#475569] leading-relaxed">{p.body}</p>
                <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#084392] group-hover:w-full transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
